/**
 * Exclude super-admin users from admin/agent report views.
 */

const SUPER_ADMIN_ROLE_CODES = new Set(['SUPER_ADMIN', 'SUPERADMIN', 'SUPER-ADMIN']);

function normalizeRoleToken(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

export function isSuperAdminRole(value) {
  const normalized = normalizeRoleToken(value);
  if (!normalized) return false;
  if (SUPER_ADMIN_ROLE_CODES.has(normalized)) return true;
  return normalized.includes('SUPER') && normalized.includes('ADMIN');
}

export function isSuperAdminUser(record) {
  if (!record || typeof record !== 'object') return false;

  const roleFields = [
    record.role,
    record.roleCode,
    record.role_code,
    record.roleName,
    record.role_name,
    record.primaryRole,
    record.primary_role,
  ];

  if (roleFields.some(isSuperAdminRole)) return true;

  const username = String(record.username || record.userName || '').trim().toLowerCase();
  const email = String(record.email || record.emailId || record.email_id || '').trim().toLowerCase();

  if (username === 'superadmin' || email.startsWith('superadmin@')) return true;

  return false;
}

export function excludeSuperAdminUsers(records) {
  if (!Array.isArray(records)) return [];
  return records.filter((record) => !isSuperAdminUser(record));
}

export function excludeSuperAdminFromReportRows(rows, options = {}) {
  if (!Array.isArray(rows)) return [];

  const {
    assigneeIdKeys = ['assigneeId', 'assignee_id', 'userId', 'user_id', 'agentId', 'agent_id'],
    nameKeys = ['assigneeName', 'assignee_name', 'agentName', 'agent_name', 'key', 'fullName', 'full_name', 'username', 'userName'],
    roleKeys = ['role', 'roleCode', 'role_code', 'roleName', 'role_name'],
    superAdminUserIds = null,
  } = options;

  const blockedIds = superAdminUserIds instanceof Set
    ? superAdminUserIds
    : new Set(Array.isArray(superAdminUserIds) ? superAdminUserIds : []);

  return rows.filter((row) => {
    if (!row || typeof row !== 'object') return false;
    if (isSuperAdminUser(row)) return false;

    for (const key of roleKeys) {
      if (isSuperAdminRole(row[key])) return false;
    }

    for (const key of nameKeys) {
      const name = String(row[key] || '').toLowerCase();
      if (name.includes('superadmin') || name.includes('super admin')) return false;
    }

    for (const key of assigneeIdKeys) {
      const id = row[key];
      if (id != null && blockedIds.has(Number(id))) return false;
    }

    return true;
  });
}

export function collectSuperAdminUserIds(users) {
  const ids = new Set();
  if (!Array.isArray(users)) return ids;
  users.forEach((user) => {
    if (!isSuperAdminUser(user)) return;
    const id = user.userId ?? user.user_id ?? user.id;
    if (id != null) ids.add(Number(id));
  });
  return ids;
}

export function excludeSuperAdminTickets(tickets) {
  return excludeSuperAdminFromReportRows(tickets, {
    assigneeIdKeys: ['assigneeId', 'assignee_id', 'assignedToId', 'assigned_to_id', 'assigneeUserId'],
    nameKeys: [
      'assigneeName',
      'assignee_name',
      'assignedToName',
      'assigned_to_name',
      'assignee',
      'agentName',
      'agent_name',
    ],
    roleKeys: ['assigneeRole', 'assignee_role', 'role', 'roleCode', 'role_code', 'assigneeRoleCode'],
  });
}
